import { useQuery } from "@tanstack/react-query";
import { DEPTH_THRESHOLD } from "@/lib/constraints";
import { constraintDirectoryKeys } from "@/lib/query";

export { DEPTH_THRESHOLD };

/**
 * The constraints directory (WI-140). Read-only: this page answers "who is
 * familiar with X", it never sets a rating — that happens on the person's own
 * profile, by an admin.
 */

export interface ConstraintDirectoryEntry {
  /** The NOS guid, lowercased. Unique across all four groupings. */
  id: string;
  name: string;
  typeKey: string;
  typeName: string;
  /** Hidden in Enable — still listed here, and still counted. */
  isHidden: boolean;
  /** Held by somebody but no longer offered by NOS. Distinct from hidden. */
  isMissingFromNos: boolean;
  /** A count, never a rating. */
  holderCount: number;
  /**
   * How many people at each rung, keyed by ordinal (WI-141). Rungs nobody
   * holds are ABSENT rather than zero — read through a default when rendering.
   */
  levelCounts: Record<number, number>;
  /** People at {@link DEPTH_THRESHOLD} or above — whether we can actually staff it. */
  depthCount: number;
}

export interface ConstraintHolder {
  personId: string;
  personName: string;
  title: string | null;
  status: string | null;
  /** NULL when this viewer may not see this person's rating. */
  level: number | null;
  levelName: string | null;
  /** Blanked on the same rule as the rating — the note is part of the judgement. */
  notes: string | null;
}

export interface ConstraintHoldersResult {
  holders: ConstraintHolder[];
  seesAllRatings: boolean;
  ratingFiltered: boolean;
}

export interface ConstraintDirectorySummary {
  /** False when NOS didn't answer — the list is only what people already hold. */
  connected: boolean;
  inUse: number;
  total: number;
  peopleRated: number;
  gaps: number;
}

/**
 * `includeInactive` changes the COUNTS, not just which rows render — a quarter
 * of the ratings belong to people who have left, so counting them overstates
 * the bench. It rides the query key so the two halves of the page can never be
 * showing different filters.
 */
export function useConstraintDirectory(includeInactive: boolean) {
  return useQuery({
    queryKey: constraintDirectoryKeys.all(includeInactive),
    queryFn: async (): Promise<{
      constraints: ConstraintDirectoryEntry[];
      summary: ConstraintDirectorySummary;
    }> => {
      const res = await fetch(
        `/api/constraint-directory${includeInactive ? "?includeInactive=1" : ""}`,
      );
      if (!res.ok) throw new Error("Failed to load the constraints directory");
      return (await res.json()).data;
    },
  });
}

/**
 * One constraint's holders, optionally narrowed to given ratings.
 *
 * The rating filter is applied SERVER-SIDE and only across people whose ratings
 * the viewer may see — otherwise ticking a rung would disclose the very numbers
 * the payload redacts. Never re-implement this filter on the client.
 */
export function useConstraintHolders(
  selected: { typeKey: string; valueId: string } | null,
  levels: number[],
  includeInactive: boolean,
) {
  return useQuery({
    queryKey: constraintDirectoryKeys.holders(
      selected?.typeKey ?? "",
      selected?.valueId ?? "",
      levels,
      includeInactive,
    ),
    queryFn: async (): Promise<ConstraintHoldersResult> => {
      const params = levels.map((l) => `level=${l}`);
      if (includeInactive) params.push("includeInactive=1");
      const qs = params.join("&");
      const res = await fetch(
        `/api/constraint-directory/${selected?.typeKey}/${selected?.valueId}/holders${
          qs ? `?${qs}` : ""
        }`,
      );
      if (!res.ok) throw new Error("Failed to load the people holding this constraint");
      return (await res.json()).data;
    },
    enabled: Boolean(selected),
  });
}
